import { useEffect, useState, type KeyboardEvent, type RefObject } from "react";
import { FOCUSABLE_SELECTOR } from "../focusable";

/** The open modals' dialogs, the topmost last. */
const openDialogs: HTMLElement[] = [];

/**
 * While a modal is open: focus moves into it, everything else on the page is inert (not
 * focusable, not read, not clickable), and when it closes focus returns to what opened it.
 * Nested modals stack: each one only releases what it made inert itself.
 *
 * The opener is read on the first render, before the dialog mounts: a control in it that
 * focuses itself on mount can't take the opener's place. If the opener is gone by the time
 * the modal closes, focus goes to the modal below, if any.
 *
 * When the focused control goes away or becomes disabled (a form's button while it sends),
 * focus falls to the page's body. A key pressed then still belongs to the topmost dialog: it
 * gets focus back and the key, so Escape and Tab keep working.
 */
export function useModalFocus(overlay: RefObject<HTMLElement | null>, dialog: RefObject<HTMLElement | null>): void {
  const [opener] = useState(() => document.activeElement);
  useEffect(() => {
    const own = overlay.current;
    const ownDialog = dialog.current;
    const madeInert = [...document.body.children].filter(
      (element): element is HTMLElement => element instanceof HTMLElement && element !== own && !element.inert,
    );
    for (const element of madeInert) element.inert = true;
    if (ownDialog !== null) openDialogs.push(ownDialog);
    // A control that focused itself on mount keeps its focus.
    if (ownDialog !== null && !ownDialog.contains(document.activeElement)) ownDialog.focus();
    const strayKey = (event: globalThis.KeyboardEvent) => {
      if (ownDialog === null || openDialogs.at(-1) !== ownDialog || event.target !== document.body) return;
      ownDialog.focus();
      ownDialog.dispatchEvent(new KeyboardEvent(event.type, event));
    };
    document.addEventListener("keydown", strayKey);
    return () => {
      document.removeEventListener("keydown", strayKey);
      for (const element of madeInert) element.inert = false;
      const index = ownDialog === null ? -1 : openDialogs.lastIndexOf(ownDialog);
      if (index !== -1) openDialogs.splice(index, 1);
      const returnTo = opener instanceof HTMLElement && opener.isConnected && opener.closest("[inert]") === null ? opener : openDialogs.at(-1);
      returnTo?.focus();
    };
  }, [overlay, dialog, opener]);
}

/**
 * The modal keys, for the topmost dialog only: a nested dialog's portal is not inside its
 * parent's DOM, but React still bubbles its key events through the parent. Escape is always
 * consumed there, so a dialog below never closes while the one on top can't; it closes the
 * dialog unless `closeDisabled`, or a control inside (an editor's find box) already used it.
 * Tab and Shift+Tab stay inside the dialog: past the last control is the first again.
 */
export function handleModalKeys(event: KeyboardEvent<HTMLElement>, closeDisabled: boolean, onClose: () => void): void {
  if (openDialogs.at(-1) !== event.currentTarget) return;
  if (event.key === "Escape") {
    event.stopPropagation();
    if (!closeDisabled && !event.defaultPrevented) onClose();
    return;
  }
  trapTab(event);
}

function trapTab(event: KeyboardEvent<HTMLElement>): void {
  if (event.key !== "Tab") return;
  const focusable = [...event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter((element) => !element.closest("[inert]"));
  const first = focusable[0];
  const last = focusable.at(-1);
  if (first === undefined || last === undefined) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === event.currentTarget)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}
